import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, FileText } from 'lucide-react';
import axios from 'axios';
import { PdfViewer } from '../../components/PdfViewer';
import { PublicPageCloseButton } from '../../components/PublicPageCloseButton';
import { usePublicDocumentView } from './hooks';
import type { PublicDocumentInstance } from './types';

export function PublicDocumentViewPage() {
  const { nonce = '' } = useParams();
  const { data, isLoading, error } = usePublicDocumentView(nonce);
  const [activeDoc, setActiveDoc] = useState<PublicDocumentInstance | null>(null);

  return (
    <div className="min-h-screen bg-slate-100 py-6 px-4">
      <div className="max-w-3xl mx-auto space-y-4">
        <header className="flex items-center justify-between">
          <div className="text-sky-700 font-bold text-lg">ReceiptRaptor</div>
          <div className="flex items-center gap-3">
            <div className="text-xs text-slate-500">Your signed documents</div>
            <PublicPageCloseButton />
          </div>
        </header>

        {isLoading && (
          <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500">
            Loading documents…
          </div>
        )}

        {error && <ErrorCard error={error} />}

        {data && !activeDoc && (
          <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            <div>
              <p className="text-xs uppercase text-slate-500 tracking-wide">From</p>
              <h1 className="text-xl font-semibold text-slate-800">{data.businessName}</h1>
              <p className="text-sm text-slate-500">Your copy of the signed documents.</p>
            </div>
            <ul className="space-y-2">
              {data.items.map((item) => (
                <li key={item.id} className="border border-slate-200 rounded-lg p-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <CheckCircle2 size={18} className="text-green-600 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium text-slate-800 truncate">{item.templateName}</div>
                      <div className="text-xs text-slate-500">
                        {item.effectiveFromUtc
                          ? `Effective ${new Date(item.effectiveFromUtc).toLocaleDateString()} – ${new Date(item.effectiveUntilUtc).toLocaleDateString()}`
                          : `Valid until ${new Date(item.effectiveUntilUtc).toLocaleDateString()}`}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setActiveDoc(item)}
                    className="bg-sky-600 text-white px-3 py-2 rounded hover:bg-sky-700 text-sm inline-flex items-center gap-2"
                  >
                    <FileText size={16} /> View
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {data && activeDoc && (
          <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            <div className="flex items-start justify-between">
              <h2 className="text-lg font-semibold text-slate-800">{activeDoc.templateName}</h2>
              <button
                type="button"
                onClick={() => setActiveDoc(null)}
                className="text-slate-600 hover:text-sky-600 text-sm"
              >
                Back
              </button>
            </div>
            <PdfViewer url={activeDoc.pdfUrl} />
            <a
              href={activeDoc.pdfUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-block text-sm text-sky-600 hover:text-sky-700"
            >
              Open / download PDF
            </a>
          </section>
        )}

        <footer className="text-center text-xs text-slate-400 pt-4">
          Keep this link to access your signed copies. It stays valid for as long as the documents are in effect.
        </footer>
      </div>
    </div>
  );
}

function ErrorCard({ error }: { error: unknown }) {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  let title = 'Something went wrong';
  let body = 'Please ask your sitter to resend your documents.';
  if (status === 404) { title = 'Link not found'; body = 'This link is invalid, has expired, or was revoked.'; }
  else if (status === 429) { title = 'Too many requests'; body = 'Please wait a minute and try again.'; }
  return (
    <div className="bg-white border border-red-200 rounded-xl p-8 text-center">
      <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
      <p className="text-sm text-slate-600 mt-2">{body}</p>
    </div>
  );
}
